// EquipmentFuelingWebform — per-equipment form rendered at /fueling/<slug>.
// Also supports a quick variant: pass equipment=null + the full equipment
// list and the form adds an equipment-picker at the top. NOTE: no live route
// currently mounts the quick variant (FuelingHub always passes a concrete
// piece); its machine-switch state isolation is locked by
// tests/static/equipment_fueling_quick_switch_static.test.js.
//
// Writes one equipment_fuelings row. Fuel type is hardcoded per piece
// (not a dropdown). DEF gallons is a separate field shown only when the
// equipment has takes_def=true. Once a valid reading is entered, every
// configured main service interval renders in one list in ascending
// checklist-cadence order; due/overdue intervals render expanded and marked
// "Due" AT their numerical position, the rest as directly-expandable
// collapsed rows so early work can be recorded during a convenient fueling.
import React from 'react';
import {projectServiceIntervals} from '../lib/equipment.js';
import {uploadEquipmentFuelingPhoto} from '../lib/equipmentFuelingPhotos.js';
import {todayCentralISO} from '../lib/dateUtils.js';
import {useOfflineRpcSubmit} from '../lib/useOfflineRpcSubmit.js';
import {withSavePrefix} from '../lib/saveErrorText.js';
import ManualsCard from '../equipment/ManualsCard.jsx';
import LockedSubmitter from './LockedSubmitter.jsx';
import StuckSubmissionsModal from './StuckSubmissionsModal.jsx';
import {useQueuedRpcRecovery} from './useQueuedRpcRecovery.jsx';
import {recoveryText} from '../lib/offlineSubmissionRecovery.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import PlannerIcon from '../components/PlannerIcon.jsx';
import {imageAltText} from '../lib/imageAlt.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import {PrivateStorageImage} from '../shared/PrivateStorageObject.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {EquipmentFuelingFormHistoryData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import type {CSSProperties} from 'react';
import type {EquipmentFuelingPhoto} from '../lib/equipmentFuelingPhotos.js';
import type {IntervalCompletion, ServiceInterval} from '../lib/equipment.js';
import type {SessionSubmitter} from './webformsRoutes.js';

type ChecklistTask = {id: string | number; label?: string | null};
type FormServiceInterval = ServiceInterval & {
  kind: string;
  hours_or_km: number;
  label: string;
  help_text?: string | null;
  tasks?: ChecklistTask[] | null;
};
type AttachmentChecklist = {
  name: string;
  kind: string;
  hours_or_km: number;
  label?: string | null;
  help_text?: string | null;
  tasks?: ChecklistTask[] | null;
};
type EveryFillupItem = {id: string | number; label?: string | null};
type FuelingEquipment = {
  id: string;
  slug: string;
  name?: string | null;
  category?: string | null;
  fuel_type?: string | null;
  tracking_unit?: string | null;
  takes_def?: boolean | null;
  service_intervals?: FormServiceInterval[] | null;
  every_fillup_items?: EveryFillupItem[] | null;
  every_fillup_help?: string | null;
  attachment_checklists?: AttachmentChecklist[] | null;
  fuel_gallons_help?: string | null;
  operator_notes?: string | null;
  manuals?: Array<{type?: string | null; path?: string | null; url?: string | null; title?: string | null}> | null;
};
type FuelingHistoryRow = {
  date?: string | null;
  team_member?: string | null;
  hours_reading?: number | string | null;
  km_reading?: number | string | null;
  service_intervals_completed?: IntervalCompletion[] | null;
  every_fillup_check?: EveryFillupItem[] | null;
};
type FuelingPhoto = EquipmentFuelingPhoto & {url?: string | null};
type FuelingInterval = {
  due: boolean;
  kind: string;
  hours_or_km: number;
  label: string;
  help_text?: string | null;
  tasks?: ChecklistTask[] | null;
  first_missed_at?: number | null;
  missed_count?: number | null;
  last_done_at?: number | null;
  last_partial?: {
    items_completed?: Array<string | number> | null;
    team_member?: string | null;
    at_reading: number;
    items_done: number;
    total: number;
  } | null;
  next_due?: number | null;
  until_due?: number | null;
};
type Streak = {count: number; oldest: {reading: number | null; name: string | null; date: string | null} | null};
type IntervalUiState = {
  k: string;
  tasks: ChecklistTask[];
  ticks: Set<string | number>;
  explicit: boolean;
  implicit: boolean;
  allTicked: boolean;
  anyTicked: boolean;
  done: boolean;
  unitShort: string;
};
type CompletionDraft = {
  interval: number;
  kind: string;
  label: string;
  completed_at: string;
  items_completed: Array<string | number>;
  total_tasks: number;
  attachment_name?: string;
  auto_from?: number;
};
type EquipmentFuelingWebformProps = {
  sb: SupabaseClient;
  equipment?: FuelingEquipment | null;
  equipmentList?: FuelingEquipment[];
  onBack?: () => void;
  sessionSubmitter?: SessionSubmitter;
};

// A stable empty value preserves the quick-switch isolation while preventing a
// no-data render from invalidating every memo that derives interval status.
const EMPTY_FUELING_HISTORY: FuelingHistoryRow[] = [];

// Recovery narrowing: everything here arrives from IndexedDB as unknown data.
function isRecordValue(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isFuelingPhoto(value: unknown): value is FuelingPhoto {
  return isRecordValue(value) && typeof value.path === 'string' && typeof value.name === 'string';
}
// Order-insensitive identity of every checklist answer; an empty task set is
// the same answer as an absent one.
function tickSignature(
  fillup: Set<string | number>,
  intervals: Set<string>,
  tasks: Record<string, Set<string | number>>,
  attachments: Record<string, Set<string | number>>,
): string {
  const set = (values: Iterable<string | number>) => Array.from(values, String).sort();
  const map = (m: Record<string, Set<string | number>>) =>
    Object.keys(m)
      .sort()
      .filter((k) => m[k] instanceof Set && m[k].size > 0)
      .map((k) => [k, set(m[k])]);
  return JSON.stringify([set(fillup), set(intervals), map(tasks), map(attachments)]);
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) return String(error.message);
  return String(error);
}

export default function EquipmentFuelingWebform({
  sb,
  equipment,
  equipmentList,
  onBack,
  sessionSubmitter,
}: EquipmentFuelingWebformProps) {
  // Lane 1 CP1: on the authenticated path the submitter is the signed-in user,
  // locked. No roster dropdown, no per-equipment operator filtering.
  const lockedName = sessionSubmitter?.name || '';
  const isQuick = !equipment;
  const [selectedEq, setSelectedEq] = React.useState<FuelingEquipment | null>(equipment || null);
  const [teamMember, setTeamMember] = React.useState(sessionSubmitter?.name || '');
  const [date, setDate] = React.useState(() => todayCentralISO());
  const [gallons, setGallons] = React.useState('');
  const [defGallons, setDefGallons] = React.useState('');
  const [reading, setReading] = React.useState('');
  const [fillupTicks, setFillupTicks] = React.useState<Set<string | number>>(new Set());
  const [intervalTicks, setIntervalTicks] = React.useState<Set<string>>(new Set()); // keys 'kind:value'
  // Non-due interval rows the operator has opened. Due intervals always
  // render expanded; this set only tracks the collapsed-by-default rows.
  const [expandedIntervals, setExpandedIntervals] = React.useState<Set<string>>(new Set()); // keys 'kind:value'
  // Per-interval task ticks: Map<'kind:value', Set<taskId>>
  const [taskTicks, setTaskTicks] = React.useState<Record<string, Set<string | number>>>({});
  // Attachment-checklist ticks: Map<'name:kind:value', Set<taskId>>
  const [attachmentTicks, setAttachmentTicks] = React.useState<Record<string, Set<string | number>>>({});
  const [photos, setPhotos] = React.useState<FuelingPhoto[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = React.useState(false);
  const [comments, setComments] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  // 'none' | 'synced' | 'queued' — replaces the prior boolean `done`.
  // synced = RPC landed online. queued = persisted in IDB; replays on
  // online event / 60s tick / next mount.
  const [doneState, setDoneState] = React.useState<'none' | 'synced' | 'queued'>('none');
  const [stuckOpen, setStuckOpen] = React.useState(false);
  const [err, setErr] = React.useState('');

  // Lane H: submit through the parent-aware offline RPC queue. The RPC owns
  // idempotency (csid) + the atomic insert+parent-bump (mig 047). Network /
  // transient failures queue to IndexedDB and replay automatically; 3 failed
  // replays surface in StuckSubmissionsModal.
  const {submit: queueSubmit, stuckRows, retryStuck, discardStuck} = useOfflineRpcSubmit('equipment_fueling');

  // Open the stuck modal automatically the first time we observe stuck rows
  // (mount or after a failed sync pass). Operator can close it; we don't
  // re-open on every render. Mirrors FuelSupply / AddFeed.
  const initialStuckShownRef = React.useRef(false);
  React.useEffect(() => {
    if (stuckRows.length > 0 && !initialStuckShownRef.current) {
      initialStuckShownRef.current = true;
      setStuckOpen(true);
    }
  }, [stuckRows.length]);

  // Recovery Open/Cancel/replacement for a queued fueling. The checklist
  // snapshot in the queued payload is the original operator intent: ticks are
  // restored into the UI from it, and an UNTOUCHED checklist re-submits the
  // ORIGINAL snapshot arrays verbatim — never a rebuild from the current
  // equipment config (labels, auto_from, items and hidden intervals would
  // otherwise drift or vanish). Photos are already-uploaded Storage
  // descriptors; they render from their existing objects and a recovery
  // replacement never deletes them (removePhoto skips the storage remove
  // while loaded, and the machine switch is refused).
  // Tick signature recorded at Open; equal at submit = checklist untouched.
  const recoveredTicks = React.useRef<string | null>(null);
  const recovery = useQueuedRpcRecovery<Record<string, unknown>>('equipment_fueling', {
    capture: () => ({
      selectedEqId: eq?.id ?? null,
      date,
      gallons,
      defGallons,
      reading,
      comments,
      fillupTicks,
      intervalTicks,
      taskTicks,
      attachmentTicks,
      expandedIntervals,
      photos,
      doneState,
    }),
    restore: (d) => {
      if (d.selectedEqId !== (eq?.id ?? null)) {
        const found = (equipmentList || []).find((x) => x.id === d.selectedEqId) || null;
        setSelectedEq(found);
      }
      setDate(String(d.date ?? ''));
      setGallons(String(d.gallons ?? ''));
      setDefGallons(String(d.defGallons ?? ''));
      setReading(String(d.reading ?? ''));
      setComments(String(d.comments ?? ''));
      setFillupTicks(d.fillupTicks instanceof Set ? d.fillupTicks : new Set());
      setIntervalTicks(d.intervalTicks instanceof Set ? d.intervalTicks : new Set());
      setTaskTicks((isRecordValue(d.taskTicks) ? d.taskTicks : {}) as Record<string, Set<string | number>>);
      setAttachmentTicks(
        (isRecordValue(d.attachmentTicks) ? d.attachmentTicks : {}) as Record<string, Set<string | number>>,
      );
      setExpandedIntervals(d.expandedIntervals instanceof Set ? d.expandedIntervals : new Set());
      setPhotos(Array.isArray(d.photos) ? d.photos.filter(isFuelingPhoto) : []);
      setDoneState('none');
      recoveredTicks.current = null;
    },
    apply: (loaded) => {
      const p = loaded.payload;
      // A queued fueling belongs to one machine. The per-equipment page cannot
      // serve another machine's draft; the quick variant selects it instead.
      const queuedEquipmentId = typeof p.equipment_id === 'string' ? p.equipment_id : '';
      if (!queuedEquipmentId) throw new Error('This fueling needs manual recovery. The report was retained.');
      if (queuedEquipmentId !== (eq?.id ?? null)) {
        if (!isQuick) throw new Error('Open this fueling on its machine page. The report was retained.');
        const found = (equipmentList || []).find((x) => x.id === queuedEquipmentId) || null;
        if (!found) throw new Error('This machine is not in the list. The fueling was retained.');
        setSelectedEq(found);
      }
      const completions = Array.isArray(p.service_intervals_completed)
        ? p.service_intervals_completed.filter(isRecordValue)
        : [];
      const fillup = Array.isArray(p.every_fillup_check) ? p.every_fillup_check.filter(isRecordValue) : [];
      const nextTaskTicks: Record<string, Set<string | number>> = {};
      const nextAttachmentTicks: Record<string, Set<string | number>> = {};
      const nextIntervalTicks = new Set<string>();
      for (const c of completions) {
        if (typeof c.kind !== 'string' || typeof c.interval !== 'number') continue;
        const items = Array.isArray(c.items_completed) ? c.items_completed : [];
        if (typeof c.attachment_name === 'string' && c.attachment_name) {
          nextAttachmentTicks[`${c.attachment_name}:${c.kind}:${c.interval}`] = new Set(items);
          continue;
        }
        const key = `${c.kind}:${c.interval}`;
        nextTaskTicks[key] = new Set(items);
        // Parent box: ticked when the original entry was parent-only, or when
        // its recorded items cover the entry's total task count (fully done).
        const total = typeof c.total_tasks === 'number' ? c.total_tasks : null;
        if (total === 0 || (total !== null && items.length >= total)) {
          nextIntervalTicks.add(key);
        }
      }
      const nextFillupTicks = new Set(
        fillup.map((f) => f.id).filter((id): id is string | number => typeof id === 'string' || typeof id === 'number'),
      );
      recoveredTicks.current = tickSignature(nextFillupTicks, nextIntervalTicks, nextTaskTicks, nextAttachmentTicks);
      setFillupTicks(nextFillupTicks);
      setTaskTicks(nextTaskTicks);
      setAttachmentTicks(nextAttachmentTicks);
      setIntervalTicks(nextIntervalTicks);
      setDate(recoveryText(p.date));
      setGallons(recoveryText(p.gallons));
      setDefGallons(recoveryText(p.def_gallons));
      setReading(recoveryText(p.hours_reading != null ? p.hours_reading : p.km_reading));
      setComments(recoveryText(p.comments));
      setPhotos(Array.isArray(p.photos) ? p.photos.filter(isFuelingPhoto) : []);
      setDoneState('none');
    },
    error: setErr,
    opened: () => {
      setStuckOpen(false);
      setErr('');
    },
    externallyBusy: submitting || uploadingPhoto,
  });
  // The submitted checklist for a recovered fueling. Untouched: the snapshot
  // arrays verbatim (completed_at follows a corrected fueling date only where
  // it was the default). Edited: the operator re-answered the visible
  // checklist, so it is re-derived, and snapshot entries the CURRENT config
  // can no longer display are kept rather than silently dropped.
  function recoveredChecklist(
    machine: FuelingEquipment,
    derivedCompleted: CompletionDraft[],
    derivedFillup: Array<Record<string, unknown>>,
  ): {completed: unknown[]; fillup: unknown[]} | null {
    const loaded = recovery.loaded;
    if (!loaded) return null;
    const p = loaded.payload;
    const originalCompleted = Array.isArray(p.service_intervals_completed)
      ? p.service_intervals_completed.filter(isRecordValue)
      : [];
    const originalFillup = Array.isArray(p.every_fillup_check) ? p.every_fillup_check.filter(isRecordValue) : [];
    if (recoveredTicks.current === tickSignature(fillupTicks, intervalTicks, taskTicks, attachmentTicks)) {
      const originalDate = p.date;
      return {
        completed: originalCompleted.map((c) =>
          c.completed_at === originalDate && date !== originalDate ? {...c, completed_at: date} : c,
        ),
        fillup: originalFillup,
      };
    }
    const shownIntervals = new Set([
      ...(machine.service_intervals || []).map((iv) => '|' + iv.kind + '|' + iv.hours_or_km),
      ...(machine.attachment_checklists || []).map((a) => a.name + '|' + a.kind + '|' + a.hours_or_km),
    ]);
    const shownFillup = new Set((machine.every_fillup_items || []).map((item) => String(item.id)));
    const identity = (c: Record<string, unknown>) =>
      (typeof c.attachment_name === 'string' ? c.attachment_name : '') +
      '|' +
      String(c.kind) +
      '|' +
      String(c.interval);
    const hiddenCompleted = originalCompleted.filter((c) => !shownIntervals.has(identity(c)));
    const hiddenFillup = originalFillup.filter((item) => !shownFillup.has(String(item.id)));
    return {completed: [...derivedCompleted, ...hiddenCompleted], fillup: [...derivedFillup, ...hiddenFillup]};
  }

  const eq = selectedEq;
  // This machine's prior fuelings, for the due-interval and miss-streak math.
  // Keyed on equipmentId, so switching machines is a KEY CHANGE: the new key has
  // no data (history reads empty on the first frame, as the old synchronous
  // setHistory([]) did) and a late response for the previous machine settles into
  // ITS OWN cache entry, which nothing is reading. That is the same isolation the
  // `cancelled` flag bought, obtained structurally instead.
  // `client: sb` keeps the read on the INJECTED client — this component takes sb
  // as a prop and imports no Supabase module, and that stays true.
  const historyResource = useRouteResource(
    RESOURCE.EQUIPMENT_FUELING_FORM_HISTORY,
    {equipmentId: eq?.id ?? null},
    {enabled: Boolean(eq), client: sb},
  );
  const history = ((historyResource.data as EquipmentFuelingFormHistoryData | null)?.history ??
    EMPTY_FUELING_HISTORY) as FuelingHistoryRow[];
  const fuelLabel = eq?.fuel_type === 'gasoline' ? 'Gasoline' : eq?.fuel_type === 'diesel' ? 'Diesel' : 'Fuel';
  const readingLabel = eq?.tracking_unit === 'km' ? 'KM' : 'Hours';

  // The submitter is the signed-in user, not a per-equipment roster choice.
  React.useEffect(() => {
    if (teamMember !== lockedName) setTeamMember(lockedName);
  }, [lockedName, teamMember]);

  // Build the completions list with reading_at_completion + team_member baked
  // in for the due-interval math (team_member is surfaced on partial rows so
  // the operator knows who left items unfinished).
  const completions = React.useMemo(() => {
    const out = [];
    for (const h of history) {
      const snap =
        h.hours_reading != null ? Number(h.hours_reading) : h.km_reading != null ? Number(h.km_reading) : null;
      if (snap == null) continue;
      for (const c of h.service_intervals_completed || []) {
        out.push({...c, reading_at_completion: snap, team_member: h.team_member || null});
      }
    }
    return out;
  }, [history]);

  const readingNum = parseFloat(reading);
  const hasReading = Number.isFinite(readingNum) && readingNum > 0;
  // Unified projection of every configured main interval. Due membership
  // comes from computeDueIntervals and status (next_due / until_due) from
  // computeIntervalStatus — both inside projectServiceIntervals; this
  // component adds no interval math of its own.
  const allIntervals = React.useMemo<FuelingInterval[]>(() => {
    if (!eq || !hasReading) return [];
    return projectServiceIntervals(eq.service_intervals || [], completions, readingNum) as unknown as FuelingInterval[];
  }, [eq, completions, readingNum, hasReading]);
  const dueIntervals = React.useMemo(() => allIntervals.filter((iv) => iv.due), [allIntervals]);

  // Every-fillup miss streaks. For each every_fillup_items[].id, count
  // consecutive prior fuelings (ordered by reading desc, falling back to
  // date) where the item was NOT in every_fillup_check. As soon as a prior
  // fueling DID tick the item, the streak ends. Time/calendar dates do not
  // factor into the math — this is purely "how many sequential fuelings,
  // most recent first, lacked this item." Display only includes the oldest
  // miss's reading + operator name as context.
  const fillupStreaks = React.useMemo<Map<string | number, Streak>>(() => {
    const out = new Map<string | number, Streak>(); // itemId -> {count, oldest:{reading, name, date}}
    if (!eq) return out;
    const items = Array.isArray(eq.every_fillup_items) ? eq.every_fillup_items : [];
    if (items.length === 0) return out;
    // Build prior-fuelings list. Order by reading desc; if reading missing,
    // fall back to date. Newest first.
    const sorted = (history || []).slice().sort((a, b) => {
      const ra = a.hours_reading != null ? Number(a.hours_reading) : a.km_reading != null ? Number(a.km_reading) : null;
      const rb = b.hours_reading != null ? Number(b.hours_reading) : b.km_reading != null ? Number(b.km_reading) : null;
      if (ra != null && rb != null && ra !== rb) return rb - ra;
      return String(b.date || '').localeCompare(String(a.date || ''));
    });
    for (const item of items) {
      let count = 0;
      let oldest: Streak['oldest'] = null;
      for (const h of sorted) {
        const ticks = Array.isArray(h.every_fillup_check) ? h.every_fillup_check : [];
        const wasTicked = ticks.some((t) => t && t.id === item.id);
        if (wasTicked) break;
        count++;
        const r =
          h.hours_reading != null ? Number(h.hours_reading) : h.km_reading != null ? Number(h.km_reading) : null;
        oldest = {reading: r, name: h.team_member || null, date: h.date || null};
      }
      if (count > 0) out.set(item.id, {count, oldest});
    }
    return out;
  }, [eq, history]);

  function toggleFillup(id: string | number) {
    const next = new Set(fillupTicks);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setFillupTicks(next);
  }
  function toggleInterval(kind: string, value: number) {
    const k = kind + ':' + value;
    const next = new Set(intervalTicks);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setIntervalTicks(next);
  }
  // Open/close a non-due interval row. Only touches expansion — selected
  // checklist items live in taskTicks/intervalTicks and survive collapse.
  function toggleExpanded(k: string) {
    setExpandedIntervals((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }
  function toggleTask(intervalKey: string, taskId: string | number) {
    setTaskTicks((prev) => {
      const current = new Set(prev[intervalKey] || []);
      if (current.has(taskId)) current.delete(taskId);
      else current.add(taskId);
      return {...prev, [intervalKey]: current};
    });
  }
  function toggleAttachmentTask(attachmentKey: string, taskId: string | number) {
    setAttachmentTicks((prev) => {
      const current = new Set(prev[attachmentKey] || []);
      if (current.has(taskId)) current.delete(taskId);
      else current.add(taskId);
      return {...prev, [attachmentKey]: current};
    });
  }
  async function uploadPhoto(file: File) {
    if (!eq) return;
    setUploadingPhoto(true);
    setErr('');
    try {
      const photo = await uploadEquipmentFuelingPhoto(sb, eq.slug, file);
      setPhotos((current) => [...current, photo]);
    } catch (error: unknown) {
      setErr(`Photo upload failed: ${errorMessage(error)}. Please try the photo again.`);
    } finally {
      setUploadingPhoto(false);
    }
  }
  async function removePhoto(idx: number) {
    const p = photos[idx];
    // A recovery replacement must never delete the ORIGINAL Storage object:
    // the retained queued report still references this path until the
    // replacement verifies. Only the replacement's descriptor is dropped.
    if (p && p.path && !recovery.loaded) {
      try {
        await sb.storage.from('equipment-maintenance-docs').remove([p.path]);
      } catch (_e) {
        /* best-effort storage cleanup */
      }
    }
    setPhotos((arr) => arr.filter((_, i) => i !== idx));
  }
  // Quick-mode machine switch. Every checklist key is equipment-CONFIG
  // scoped (fillup item ids, interval 'kind:value', attachment
  // 'name:kind:value'), NOT equipment-INSTANCE scoped — two machines sharing
  // a key would silently inherit each other's ticks, and the projection/
  // submit paths would record machine A's selections against machine B.
  // Clear all equipment-scoped form state before the new machine can render
  // or submit. Unsubmitted photos were uploaded under the previous machine's
  // storage prefix (fueling/<slug>/…): best-effort remove them (mirrors
  // removePhoto) so machine A's photos can neither attach to machine B nor
  // linger as orphans. Submitted photos are never in state here — the done
  // screen's Log Another clears photos before the picker can change again.
  function resetEquipmentScopedState() {
    setFillupTicks(new Set());
    setIntervalTicks(new Set());
    setTaskTicks({});
    setAttachmentTicks({});
    setExpandedIntervals(new Set());
    const stale = photos;
    setPhotos([]);
    for (const p of stale) {
      if (p && p.path) {
        sb.storage
          .from('equipment-maintenance-docs')
          .remove([p.path])
          .catch(() => {});
      }
    }
  }
  // An interval counts as explicitly fully done when its parent box is
  // ticked (no-task intervals) or every task is ticked — the same rule
  // submit() uses to build fullyDoneExplicit.
  function isFullyTickedExplicit(iv: FormServiceInterval | FuelingInterval) {
    const k = iv.kind + ':' + iv.hours_or_km;
    const tasks = Array.isArray(iv.tasks) ? iv.tasks : [];
    if (tasks.length === 0) return intervalTicks.has(k);
    const ticks = taskTicks[k] instanceof Set ? taskTicks[k] : new Set();
    return ticks.size >= tasks.length;
  }
  // If the team fully ticks a big interval — due or recorded early while not
  // yet due — the divisor rule implicitly covers any smaller configured
  // interval that divides it. Surface this visually.
  function isImplicitlyCompleted(kind: string, value: number) {
    if (!eq) return false;
    if (intervalTicks.has(kind + ':' + value)) return false;
    for (const iv of eq.service_intervals || []) {
      if (iv.kind !== kind) continue;
      if (iv.hours_or_km === value) continue;
      if (iv.hours_or_km % value !== 0) continue;
      if (isFullyTickedExplicit(iv)) return true;
    }
    return false;
  }

  // ---- Service-interval rendering ----------------------------------------
  // ONE checklist renderer serves due and non-due intervals so their
  // completion behavior cannot drift: identical state keys (taskTicks /
  // intervalTicks), handlers, and implicit-completion rule. Only cosmetic
  // colors and the due-only copy differ between the two row types.
  function intervalUiState(iv: FuelingInterval): IntervalUiState {
    const k = iv.kind + ':' + iv.hours_or_km;
    const tasks = Array.isArray(iv.tasks) ? iv.tasks : [];
    const ticks = taskTicks[k] instanceof Set ? taskTicks[k] : new Set<string | number>();
    const explicit = intervalTicks.has(k);
    const implicit = isImplicitlyCompleted(iv.kind, iv.hours_or_km);
    const allTicked = tasks.length > 0 && ticks.size >= tasks.length;
    const anyTicked = ticks.size > 0;
    const done = (tasks.length === 0 ? explicit : allTicked) || implicit;
    const unitShort = iv.kind === 'km' ? 'km' : 'h';
    return {k, tasks, ticks, explicit, implicit, allTicked, anyTicked, done, unitShort};
  }

  function renderIntervalChecklist(
    iv: FuelingInterval,
    ui: IntervalUiState,
    {due, dividerColor}: {due: boolean; dividerColor: string},
  ) {
    const {k, tasks, ticks, explicit, implicit, allTicked, anyTicked} = ui;
    return (
      <>
        {/* Field-level help text from Podio (torque specs, gap specs, etc.) */}
        {iv.help_text && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--text-2)',
              background: 'var(--warn-soft)',
              border: '1px solid #fde68a',
              borderRadius: 10,
              padding: '6px 8px',
              marginBottom: 8,
              fontStyle: 'italic',
              whiteSpace: 'pre-wrap',
            }}
          >
            {iv.help_text}
          </div>
        )}
        {tasks.length === 0 ? (
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 8px',
              borderRadius: 10,
              border: '1px solid ' + (explicit ? 'var(--brand)' : 'var(--border)'),
              background: '#fff',
              cursor: implicit ? 'not-allowed' : 'pointer',
              fontSize: 12,
              opacity: implicit ? 0.45 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={explicit}
              disabled={implicit}
              onChange={() => toggleInterval(iv.kind, iv.hours_or_km)}
              style={{
                margin: 0,
                flexShrink: 0,
                width: 18,
                height: 18,
                padding: 0,
                border: '1px solid var(--border-strong)',
              }}
            />
            <span style={{color: explicit ? 'var(--brand)' : 'var(--ink)', fontWeight: explicit ? 600 : 500}}>
              Mark this service done
            </span>
          </label>
        ) : (
          <>
            {/* Sub-task list (when this interval has tasks) */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                marginLeft: 0,
                borderTop: '1px solid ' + dividerColor,
                paddingTop: 8,
              }}
            >
              {tasks.map((t) => {
                const ticked = ticks.has(t.id);
                return (
                  <label
                    key={t.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      padding: '6px 8px',
                      borderRadius: 10,
                      border: '1px solid ' + (ticked ? 'var(--brand)' : 'var(--border)'),
                      background: '#fff',
                      cursor: implicit ? 'not-allowed' : 'pointer',
                      fontSize: 12,
                      opacity: implicit ? 0.45 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={ticked}
                      disabled={implicit}
                      onChange={() => toggleTask(k, t.id)}
                      style={{
                        margin: '2px 0 0',
                        flexShrink: 0,
                        width: 16,
                        height: 16,
                        padding: 0,
                        border: '1px solid var(--border-strong)',
                      }}
                    />
                    <span style={{color: ticked ? 'var(--brand)' : 'var(--ink)', fontWeight: ticked ? 600 : 500}}>
                      {t.label}
                    </span>
                  </label>
                );
              })}
            </div>
            {(due || anyTicked) && (
              <div
                style={{
                  fontSize: 11,
                  color: anyTicked ? 'var(--info-ink)' : due ? 'var(--danger-ink)' : 'var(--ink-muted)',
                  marginTop: 6,
                  fontWeight: 600,
                }}
              >
                {ticks.size} of {tasks.length} tasks ticked
                {allTicked
                  ? ' · full completion'
                  : anyTicked
                    ? due
                      ? ' · partial (will remain due)'
                      : ' · partial'
                    : ''}
              </div>
            )}
          </>
        )}
      </>
    );
  }

  // Due interval — always expanded, prominent, explicitly marked "Due".
  function renderDueInterval(iv: FuelingInterval) {
    const ui = intervalUiState(iv);
    const {k, done, unitShort} = ui;
    return (
      <div
        key={k}
        data-interval-row={k}
        data-interval-state="due"
        style={{
          borderRadius: 10,
          // R09: no edge stripe, and Q03 never allowed an ok-colored one. The
          // label below is already ok/danger ink, and a due row takes the tint.
          background: done ? '#fff' : 'var(--danger-soft)',
          border: '1px solid var(--border-strong)',
          padding: '12px 14px',
        }}
      >
        {/* Header line (always shown) */}
        <div style={{marginBottom: 8}}>
          <div style={{fontWeight: 700, color: done ? 'var(--info-ink)' : 'var(--danger-ink)', fontSize: 14}}>
            {iv.label}
            <span
              style={{
                display: 'inline-block',
                marginLeft: 8,
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                color: 'var(--danger-ink)',
                background: 'var(--danger-soft)',
                border: '1px solid #fca5a5',
                borderRadius: 10,
                padding: '1px 8px',
                verticalAlign: 'middle',
              }}
            >
              Due
            </span>
          </div>
          <div style={{fontSize: 11, color: 'var(--ink-muted)', marginTop: 3}}>
            {(iv.missed_count ?? 0) > 1
              ? `Missed ${iv.missed_count ?? 0} times since ${(iv.first_missed_at ?? 0).toLocaleString()}${unitShort}`
              : `Passed ${(iv.first_missed_at ?? 0).toLocaleString()}${unitShort}`}
            {iv.last_done_at
              ? ` · last full done at ${iv.last_done_at.toLocaleString()}${unitShort}`
              : ' · never fully completed'}
          </div>
          {iv.last_partial &&
            (() => {
              // Only rendered when no full completion exists after this
              // partial (filtered in computeDueIntervals). Resolve the
              // ticked-item IDs against current task labels to show
              // exactly what was missed and by whom.
              const doneIds = new Set(iv.last_partial.items_completed || []);
              const missing = (Array.isArray(iv.tasks) ? iv.tasks : []).filter((t) => !doneIds.has(t.id));
              const who = iv.last_partial.team_member || 'someone';
              return (
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--warn-ink)',
                    marginTop: 4,
                    background: 'var(--warn-soft)',
                    border: '1px solid #fde68a',
                    borderRadius: 10,
                    padding: '5px 8px',
                  }}
                >
                  <strong>
                    Partial at {iv.last_partial.at_reading.toLocaleString()}
                    {unitShort}
                  </strong>{' '}
                  by {who} — {iv.last_partial.items_done}/{iv.last_partial.total} done.
                  {missing.length > 0 && (
                    <>
                      {' '}
                      Missing: <span style={{fontStyle: 'italic'}}>{missing.map((m) => m.label).join(', ')}</span>
                    </>
                  )}
                </div>
              );
            })()}
        </div>
        {renderIntervalChecklist(iv, ui, {due: true, dividerColor: done ? 'var(--ok-ink)' : 'var(--danger)'})}
      </div>
    );
  }

  // Non-due interval — collapsed row that opens directly (click / Enter /
  // Space on the disclosure button). Neutral context only: next scheduled
  // milestone + remaining distance. Must never read as due or missed work.
  function renderUpcomingInterval(iv: FuelingInterval) {
    const ui = intervalUiState(iv);
    const {k, tasks, ticks, explicit, implicit, anyTicked, done, unitShort} = ui;
    const expanded = expandedIntervals.has(k);
    const bodyId = 'svc-body-' + k.replace(/[^a-zA-Z0-9_-]/g, '-');
    return (
      <div
        key={k}
        data-interval-row={k}
        data-interval-state="upcoming"
        style={{
          borderRadius: 10,
          background: done ? 'var(--ok-soft)' : 'var(--surface-2)',
          border: '1px solid var(--border)',
        }}
      >
        <button
          type="button"
          onClick={() => toggleExpanded(k)}
          aria-expanded={expanded}
          aria-controls={bodyId}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '12px 14px',
            minHeight: 44,
            background: 'none',
            border: 'none',
            borderRadius: 10,
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        >
          <span aria-hidden="true" style={{fontSize: 12, color: 'var(--ink-muted)', flexShrink: 0, marginTop: 2}}>
            {expanded ? '▾' : '▸'}
          </span>
          <span style={{flex: 1, minWidth: 0}}>
            <span
              style={{display: 'block', fontWeight: 700, color: done ? 'var(--info-ink)' : 'var(--ink)', fontSize: 14}}
            >
              {iv.label}
            </span>
            <span style={{display: 'block', fontSize: 11, color: 'var(--ink-muted)', marginTop: 3}}>
              Next at {(iv.next_due ?? 0).toLocaleString()}
              {unitShort} · {(iv.until_due ?? 0).toLocaleString()}
              {unitShort} remaining
            </span>
          </span>
          {(anyTicked || explicit || implicit) && (
            <span style={{flexShrink: 0, fontSize: 11, fontWeight: 600, color: 'var(--info-ink)', marginTop: 2}}>
              {implicit ? '✓ covered' : tasks.length === 0 ? '✓ selected' : `${ticks.size} of ${tasks.length} selected`}
            </span>
          )}
        </button>
        {expanded && (
          <div id={bodyId} style={{padding: '0 14px 12px 36px'}}>
            {renderIntervalChecklist(iv, ui, {due: false, dividerColor: done ? 'var(--ok-ink)' : 'var(--border)'})}
          </div>
        )}
      </div>
    );
  }

  async function submit() {
    setErr('');
    if (!eq) {
      setErr('Pick equipment.');
      return;
    }
    if (!teamMember) {
      setErr('Pick a team member.');
      return;
    }
    if (!date) {
      setErr('Date required.');
      return;
    }
    if (!gallons || parseFloat(gallons) <= 0) {
      setErr(fuelLabel + ' gallons required.');
      return;
    }
    // Reading required — the RPC rejects a missing/zero reading and the
    // parent equipment row needs it to advance via the GREATEST update.
    if (!hasReading) {
      setErr('Current ' + readingLabel + ' required.');
      return;
    }

    // Check Oil enforcement: every non-ATV, non-Toro piece must have its
    // oil-check ticked before the submit goes through. "Oil" match is broad
    // so "CHECK OIL" and "CHECK ENGINE OIL LEVEL" both count.
    const exemptFromOil = eq.category === 'atvs' || eq.slug === 'toro';
    if (!exemptFromOil) {
      const items = Array.isArray(eq.every_fillup_items) ? eq.every_fillup_items : [];
      const oilItem = items.find((it) => /oil/i.test(it.label || ''));
      if (oilItem && !fillupTicks.has(oilItem.id)) {
        setErr('"' + oilItem.label + '" must be ticked before submitting.');
        return;
      }
    }
    // Build service_intervals_completed. Every configured main interval —
    // due or recorded early while not yet due — may contribute a completion
    // if either (a) the parent box was ticked, OR (b) any of its sub-tasks
    // were ticked. Record which task ids were ticked so the due-interval
    // math can decide "full vs partial." One entry per interval identity
    // (kind + hours_or_km): explicit selections and divisor-generated
    // completions are deduplicated below.
    const mainByKey = new Map<string, CompletionDraft>(); // 'kind:value' -> completion entry
    const fullyDoneExplicit = new Set<string>(); // intervals that had ALL tasks ticked
    for (const iv of allIntervals) {
      const key = iv.kind + ':' + iv.hours_or_km;
      const ticks = taskTicks[key] instanceof Set ? taskTicks[key] : new Set<string | number>();
      const parentTicked = intervalTicks.has(key);
      const tasks = Array.isArray(iv.tasks) ? iv.tasks : [];
      const items = Array.from(ticks);
      const fullyDone = tasks.length === 0 ? parentTicked : tasks.length > 0 && items.length >= tasks.length;
      if (!parentTicked && items.length === 0) continue; // nothing ticked for this interval
      mainByKey.set(key, {
        interval: iv.hours_or_km,
        kind: iv.kind,
        label: iv.label,
        completed_at: date,
        items_completed: items,
        total_tasks: tasks.length,
      });
      if (fullyDone) fullyDoneExplicit.add(key);
    }
    // Divisor rule: a fully-done big interval also counts as fully-done for
    // any smaller interval it divides (e.g. 1000h fully done → 500/250/100
    // also marked done at the same reading) — including when the big interval
    // was completed early from a non-due row. When the smaller interval was
    // ALSO explicitly ticked (partially), upgrade that entry in place instead
    // of appending a duplicate entry for the same interval identity.
    for (const key of fullyDoneExplicit) {
      const [kind, vStr] = key.split(':');
      const v = parseInt(vStr, 10);
      for (const iv of eq.service_intervals || []) {
        if (iv.kind !== kind) continue;
        if (iv.hours_or_km === v) continue;
        if (v % iv.hours_or_km !== 0) continue;
        const kk = iv.kind + ':' + iv.hours_or_km;
        if (fullyDoneExplicit.has(kk)) continue;
        const subTasks = (Array.isArray(iv.tasks) ? iv.tasks : []) as ChecklistTask[];
        const existing = mainByKey.get(kk);
        if (existing && existing.auto_from != null) continue; // already covered by another parent
        if (existing) {
          existing.auto_from = v;
          existing.items_completed = subTasks.map((t) => t.id);
          existing.total_tasks = subTasks.length;
        } else {
          mainByKey.set(kk, {
            interval: iv.hours_or_km,
            kind: iv.kind,
            label: iv.label,
            completed_at: date,
            auto_from: v,
            items_completed: subTasks.map((t) => t.id),
            total_tasks: subTasks.length,
          });
        }
      }
    }
    const completed = Array.from(mainByKey.values());

    // Attachment completions — stored in service_intervals_completed with an
    // attachment_name key so the dashboard can distinguish from main intervals.
    for (const a of eq.attachment_checklists || []) {
      const key = a.name + ':' + a.kind + ':' + a.hours_or_km;
      const ticks = attachmentTicks[key] instanceof Set ? attachmentTicks[key] : new Set<string | number>();
      if (ticks.size === 0) continue;
      completed.push({
        interval: a.hours_or_km,
        kind: a.kind,
        label: a.label || '',
        attachment_name: a.name,
        completed_at: date,
        items_completed: Array.from(ticks),
        total_tasks: (a.tasks || []).length,
      });
    }

    const derivedFillup = Array.from(fillupTicks).map((id) => {
      const item = (eq.every_fillup_items || []).find((x) => x.id === id);
      return {id, label: item?.label || id, ok: true};
    });
    // A recovered fueling keeps its original checklist meaning (see
    // recoveredChecklist); a fresh fueling submits exactly what was derived.
    const recovered = recoveredChecklist(eq, completed, derivedFillup);
    const fillup = recovered ? recovered.fillup : derivedFillup;
    const serviceIntervalsCompleted = recovered ? recovered.completed : completed;

    const defNum = parseFloat(defGallons);
    // Mig 047 SECURITY DEFINER RPC, reached through useOfflineRpcSubmit. The
    // RPC INSERTs the equipment_fuelings row AND updates
    // equipment.current_<unit> atomically with anon EXECUTE — the prior
    // pattern (synchronous .insert + silent .update on equipment) was broken
    // under prod RLS because anon has no UPDATE policy on equipment; the
    // parent row drifted behind every public submission. The RPC's
    // GREATEST/only-go-forward semantic means a submission can only raise
    // current_<unit> — admin corrections downward still go through the
    // authenticated /fleet detail page.
    //
    // The hook mints a stable client_submission_id + parent id once at
    // submit() entry and persists them, so an offline-queued retry replays
    // byte-identical args (the registry's buildArgs is pure over this
    // payload). We pass the fully-computed record minus identity; the
    // registry stamps id + csid.
    const payload = {
      equipment_id: eq.id,
      date,
      team_member: teamMember,
      // A recovered fueling keeps the fuel type it was logged with.
      fuel_type:
        recovery.loaded && 'fuel_type' in recovery.loaded.payload
          ? (recovery.loaded.payload.fuel_type ?? null)
          : eq.fuel_type || null,
      gallons: parseFloat(gallons),
      def_gallons: Number.isFinite(defNum) && defNum > 0 ? defNum : null,
      fuel_cost_per_gal: null,
      hours_reading: eq.tracking_unit === 'hours' ? readingNum : null,
      km_reading: eq.tracking_unit === 'km' ? readingNum : null,
      every_fillup_check: fillup,
      service_intervals_completed: serviceIntervalsCompleted,
      photos,
      comments: comments || null,
      source: 'fuel_log_webform',
      podio_source_app: null,
    };
    if (!(await recovery.beginSubmit())) return;
    setSubmitting(true);
    try {
      const result = await queueSubmit(payload, recovery.options);
      if (!recovery.finish(true)) setDoneState(result.state);
    } catch (e: unknown) {
      // Schema/validation error — useOfflineRpcSubmit throws on PGRST/22*/23*/
      // P0001 instead of silently queuing a deterministically-bad submission.
      setErr(withSavePrefix(errorMessage(e), 'Save failed: '));
    } finally {
      recovery.finish();
      setSubmitting(false);
    }
  }

  const wfBg = {
    minHeight: '100vh',
    background: 'var(--bg-page)',
    padding: '1rem',
    fontFamily: 'inherit',
  };
  const cardS = {
    background: 'white',
    borderRadius: 12,
    padding: '18px 20px',
    marginBottom: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,.08)',
  };
  const inpS: CSSProperties = {
    fontFamily: 'inherit',
    fontSize: 15,
    padding: '10px 12px',
    border: '1px solid var(--border-strong)',
    borderRadius: 12,
    width: '100%',
    outline: 'none',
    background: 'white',
    color: 'var(--ink)',
    boxSizing: 'border-box',
  };
  const lblS = {
    display: 'block',
    fontSize: 13,
    color: 'var(--ink)',
    marginBottom: 5,
    fontWeight: 600,
  };
  // Icon + title link HOME (/) — a real anchor so normal browser behavior,
  // keyboard activation, and link previews all work. Deliberately NOT the
  // Back action (Back returns to the Equipment hub). The subtitle stays
  // plain text. No textDecoration/underline, and focus visibility is left to
  // the browser default (never suppressed).
  const logoEl = (
    <div style={{textAlign: 'center', marginBottom: 20}}>
      <a
        href="/"
        data-fueling-logo-link="1"
        style={{
          fontSize: 18,
          fontWeight: 800,
          color: 'var(--ink)',
          letterSpacing: -0.3,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          textDecoration: 'none',
        }}
      >
        <PlannerIcon iconKey="fueling" size={22} />
        <span>WCF Planner</span>
      </a>
      <div style={{fontSize: 12, color: 'var(--ink-muted)', marginTop: 2}}>Fueling Log</div>
    </div>
  );

  if (doneState !== 'none') {
    const queued = doneState === 'queued';
    return (
      <div style={wfBg}>
        <div
          data-submit-state={doneState}
          style={{maxWidth: 480, margin: '0 auto', paddingTop: '2rem', textAlign: 'center'}}
        >
          {logoEl}
          <div
            style={{fontSize: 20, fontWeight: 700, color: queued ? 'var(--warn-ink)' : 'var(--ink)', marginBottom: 8}}
          >
            {queued ? 'Saved on this device' : 'Fueling saved'}
          </div>
          <div style={{fontSize: 14, color: 'var(--ink-muted)', marginBottom: queued ? 12 : 28}}>
            {eq ? eq.name : ''} · {gallons} gal {fuelLabel}
            {defGallons ? ' + ' + defGallons + ' gal DEF' : ''}
          </div>
          {queued && (
            <div
              style={{
                fontSize: 12,
                color: 'var(--text-2)',
                marginBottom: 28,
                lineHeight: 1.5,
                background: 'var(--warn-soft)',
                border: '1px solid #fde68a',
                borderRadius: 10,
                padding: '8px 12px',
                textAlign: 'left',
              }}
            >
              No connection right now. Your fueling is queued and will sync as soon as the device is back online. The
              equipment reading updates once it syncs.
            </div>
          )}
          <button
            onClick={() => {
              if (isQuick) setSelectedEq(null);
              setDoneState('none');
              setGallons('');
              setDefGallons('');
              setReading('');
              setComments('');
              setFillupTicks(new Set());
              setIntervalTicks(new Set());
              // Interval task ticks + open rows are per-record — a fresh log
              // must not silently re-attach the previous record's checklist.
              setTaskTicks({});
              setExpandedIntervals(new Set());
              // Photos now belong to the SAVED record. Dropping them from
              // state stops them re-attaching to the next log AND keeps the
              // quick-mode switch cleanup (which storage-removes unsubmitted
              // photos) from ever touching a submitted record's files.
              setPhotos([]);
            }}
            style={{
              width: '100%',
              padding: 14,
              borderRadius: 10,
              border: 'none',
              background: '#187A54',
              color: 'white',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginBottom: 10,
            }}
          >
            Log Another
          </button>
          <button
            onClick={onBack}
            style={{
              width: '100%',
              padding: 14,
              borderRadius: 10,
              border: '1px solid var(--border-strong)',
              background: 'white',
              color: 'var(--ink)',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Back to Hub
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={wfBg}>
      <div style={{maxWidth: 520, margin: '0 auto', paddingTop: '1rem'}}>
        {logoEl}
        {recovery.banner}
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--ink-muted)',
            fontSize: 13,
            cursor: 'pointer',
            marginBottom: 12,
            padding: 0,
            fontFamily: 'inherit',
          }}
        >
          {'‹ Back'}
        </button>

        {stuckRows.length > 0 && (
          <button
            onClick={() => setStuckOpen(true)}
            data-stuck-button="1"
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: 10,
              border: '1px solid #fde68a',
              background: 'var(--warn-soft)',
              color: 'var(--warn-ink)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginBottom: 12,
              textAlign: 'left',
            }}
          >
            {stuckRows.length} unsynced fueling{stuckRows.length === 1 ? '' : 's'} — tap to review
          </button>
        )}

        {/* Header tile: what piece am I fueling? */}
        <div style={cardS}>
          {isQuick && (
            <div style={{marginBottom: 12}}>
              <label style={lblS}>Equipment *</label>
              <select
                value={eq?.id || ''}
                onChange={(e) => {
                  // An open recovery holds this machine's snapshot; the switch
                  // cleanup storage-removes unsubmitted photos, which during a
                  // recovery are the ORIGINAL record's objects. Cancel first.
                  if (recovery.loaded) {
                    setErr('Cancel recovery before switching machines. The report was retained.');
                    return;
                  }
                  const found = (equipmentList || []).find((x) => x.id === e.target.value) || null;
                  const changed = (found?.id || null) !== (eq?.id || null);
                  if (changed) resetEquipmentScopedState();
                  setSelectedEq(found);
                }}
                style={inpS}
              >
                <option value="">Select equipment...</option>
                {(equipmentList || []).map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {eq &&
            !isQuick &&
            (() => {
              const lastFueling = (history || []).find((h) => h.hours_reading != null || h.km_reading != null);
              const lastReadingNum = lastFueling
                ? eq.tracking_unit === 'km'
                  ? lastFueling.km_reading
                  : lastFueling.hours_reading
                : null;
              const lastUnit = eq.tracking_unit === 'km' ? 'km' : 'h';
              const fmtDate = (s: string | null | undefined) => {
                if (!s) return '';
                const [y, m, d] = String(s).slice(0, 10).split('-');
                return `${m}/${d}/${y.slice(2)}`;
              };
              return (
                <div>
                  <div style={{fontSize: 18, fontWeight: 700, color: 'var(--ink)'}}>{eq.name}</div>
                  <div style={{fontSize: 12, color: 'var(--ink-muted)', marginTop: 2}}>
                    {eq.fuel_type ? eq.fuel_type.charAt(0).toUpperCase() + eq.fuel_type.slice(1) : ''}
                    {eq.takes_def ? ' + DEF' : ''} · tracks {readingLabel}
                  </div>
                  {lastFueling && lastReadingNum != null && (
                    <div style={{fontSize: 12, color: 'var(--ink)', marginTop: 6, fontWeight: 600}}>
                      Last reading: {Number(lastReadingNum).toLocaleString()} {lastUnit}
                      {lastFueling.team_member ? ` · ${lastFueling.team_member}` : ''}
                      {lastFueling.date ? ` · ${fmtDate(lastFueling.date)}` : ''}
                    </div>
                  )}
                </div>
              );
            })()}
        </div>

        {/* Top-of-form operator notes (between-fillup maintenance guidance). */}
        {eq && eq.operator_notes && (
          <div style={{...cardS, background: 'var(--warn-soft)', borderColor: 'var(--warn-stripe)'}}>
            <div style={{fontSize: 12, fontWeight: 700, color: 'var(--warn-ink)', marginBottom: 6}}>Operator note</div>
            <div style={{fontSize: 12, color: 'var(--text-2)', whiteSpace: 'pre-wrap', fontStyle: 'italic'}}>
              {eq.operator_notes}
            </div>
          </div>
        )}

        {/* Expandable Manuals & Videos card — reference materials for operators. */}
        <ManualsCard equipment={eq} />

        {/* Date + Team Member on their own rows */}
        <div style={cardS}>
          <div style={{marginBottom: 12}}>
            <label style={lblS}>Date *</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inpS} />
          </div>
          <div>
            <LockedSubmitter name={lockedName} label="Team member" labelStyle={lblS} />
          </div>
        </div>

        {/* Fuel + DEF (per-piece, not a dropdown). */}
        {eq && eq.fuel_type && (
          <div style={cardS}>
            <div style={{marginBottom: 12}}>
              <label style={lblS}>{fuelLabel} gallons *</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={gallons}
                onChange={(e) => setGallons(e.target.value)}
                style={inpS}
              />
              {eq.fuel_gallons_help && (
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--text-2)',
                    marginTop: 6,
                    fontStyle: 'italic',
                    background: 'var(--warn-soft)',
                    border: '1px solid #fde68a',
                    borderRadius: 10,
                    padding: '6px 8px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {eq.fuel_gallons_help}
                </div>
              )}
            </div>
            {eq.takes_def && (
              <div>
                <label style={lblS}>DEF gallons</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={defGallons}
                  onChange={(e) => setDefGallons(e.target.value)}
                  style={inpS}
                />
              </div>
            )}
          </div>
        )}

        {/* Reading (hours / km). Required for service-interval math. */}
        {eq && (
          <div style={cardS}>
            <label style={lblS}>{readingLabel} *</label>
            <input
              type="number"
              min="0"
              step="0.1"
              value={reading}
              onChange={(e) => setReading(e.target.value)}
              style={inpS}
            />
            <div style={{fontSize: 11, color: 'var(--ink-faint)', marginTop: 6}}>
              Every-fillup and service-interval checklists appear below once you enter this.
            </div>
          </div>
        )}

        {/* Every-fillup checks — hidden until the team enters a reading so
            the checklist section doesn't open before we know what to show. */}
        {eq && hasReading && (eq.every_fillup_items || []).length > 0 && (
          <div style={cardS}>
            <div
              style={{fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: eq.every_fillup_help ? 4 : 10}}
            >
              Every-fillup checks
            </div>
            {eq.every_fillup_help && (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text-2)',
                  background: 'var(--warn-soft)',
                  border: '1px solid #fde68a',
                  borderRadius: 10,
                  padding: '8px 10px',
                  marginBottom: 10,
                  fontStyle: 'italic',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {eq.every_fillup_help}
              </div>
            )}
            <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
              {(eq.every_fillup_items || []).map((item) => {
                // Mark oil-check items as required for non-ATV / non-Toro pieces.
                const isOil = /oil/i.test(item.label || '');
                const exemptFromOil = eq.category === 'atvs' || eq.slug === 'toro';
                const required = isOil && !exemptFromOil;
                const unticked = !fillupTicks.has(item.id);
                const needsAttention = required && unticked;
                const bd = needsAttention
                  ? 'var(--danger)'
                  : fillupTicks.has(item.id)
                    ? 'var(--brand)'
                    : 'var(--border-strong)';
                const bg = needsAttention ? 'var(--danger-soft)' : '#fff';
                const streak = fillupStreaks.get(item.id);
                const unitShort = eq.tracking_unit === 'km' ? 'km' : 'h';
                return (
                  <div key={item.id}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: bg,
                        cursor: 'pointer',
                        border: '1px solid ' + bd,
                        fontSize: 13,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={fillupTicks.has(item.id)}
                        onChange={() => toggleFillup(item.id)}
                        style={{
                          margin: 0,
                          flexShrink: 0,
                          width: 18,
                          height: 18,
                          padding: 0,
                          border: '1px solid var(--border-strong)',
                        }}
                      />
                      <span
                        style={{
                          color: fillupTicks.has(item.id)
                            ? 'var(--brand)'
                            : needsAttention
                              ? 'var(--danger-ink)'
                              : 'var(--ink)',
                          fontWeight: fillupTicks.has(item.id) ? 600 : needsAttention ? 600 : 500,
                        }}
                      >
                        {item.label}
                        {required && <span style={{color: 'var(--danger-ink)', marginLeft: 4}}>*</span>}
                      </span>
                    </label>
                    {streak && streak.count > 0 && !fillupTicks.has(item.id) && (
                      <div
                        style={{
                          margin: '4px 0 2px 30px',
                          fontSize: 11,
                          color: 'var(--warn-ink)',
                          background: 'var(--warn-soft)',
                          border: '1px solid #fde68a',
                          borderRadius: 10,
                          padding: '5px 8px',
                        }}
                      >
                        <strong>
                          Not done at last {streak.count} fillup{streak.count === 1 ? '' : 's'}
                        </strong>
                        {streak.oldest && streak.oldest.reading != null && (
                          <>
                            {' '}
                            · oldest {streak.oldest.reading.toLocaleString()}
                            {unitShort}
                          </>
                        )}
                        {streak.oldest && streak.oldest.name && <> by {streak.oldest.name}</>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Service intervals — every configured main interval in ONE list,
            globally ordered by ascending checklist cadence. Due rows render
            expanded and marked "Due" at their numerical position; the rest
            are directly expandable collapsed rows for recording early work. */}
        {eq &&
          hasReading &&
          allIntervals.length > 0 &&
          (() => {
            // Most recent full completion across the due intervals — shown in
            // the blurb so the operator sees where the machine last stood.
            const mostRecentFull = dueIntervals
              .filter((iv) => iv.last_done_at)
              .sort((a, b) => (b.last_done_at || 0) - (a.last_done_at || 0))[0];
            return (
              <div style={cardS}>
                <div style={{fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 4}}>
                  Service intervals
                </div>
                {dueIntervals.length > 0 ? (
                  <div style={{fontSize: 11, color: 'var(--ink-muted)', marginBottom: 12}}>
                    <span style={{color: 'var(--danger-ink)', fontWeight: 700}}>Service due.</span> Based on{' '}
                    {readingLabel.toLowerCase()} {readingNum.toLocaleString()} + prior completions. Tick any service you
                    performed during this fill.
                    {mostRecentFull && (
                      <>
                        {' '}
                        Last {mostRecentFull.hours_or_km}
                        {mostRecentFull.kind === 'km' ? '-km' : '-hour'} checklist done at{' '}
                        {mostRecentFull.last_done_at?.toLocaleString()}
                        {mostRecentFull.kind === 'km' ? 'km' : 'h'}.
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        background: 'var(--ok-soft)',
                        borderRadius: 10,
                        padding: '8px 12px',
                        marginBottom: 8,
                      }}
                    >
                      <div style={{fontSize: 13, fontWeight: 600, color: 'var(--ok-ink)'}}>
                        ✓ No service due at {readingNum.toLocaleString()} {readingLabel.toLowerCase()}.
                      </div>
                    </div>
                    <div style={{fontSize: 11, color: 'var(--ink-muted)', marginBottom: 12}}>
                      Open any interval below to record service work done early.
                    </div>
                  </>
                )}
                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                  {/* ONE list in the projection's global ascending cadence
                      order — due rows render expanded at their numerical
                      position, never hoisted above smaller intervals. */}
                  {allIntervals.map((iv) => (iv.due ? renderDueInterval(iv) : renderUpcomingInterval(iv)))}
                </div>
              </div>
            );
          })()}

        {/* Attachment-specific checklists (Ventrac — Tough Cut / AERO-Vator /
            Landscape Rake). Only shown when this piece has attachments and a
            reading is entered. Tick only the attachment you used. */}
        {eq && hasReading && Array.isArray(eq.attachment_checklists) && eq.attachment_checklists.length > 0 && (
          <div style={cardS}>
            <div style={{fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 4}}>
              Attachment-specific checklists
            </div>
            <div style={{fontSize: 11, color: 'var(--ink-muted)', marginBottom: 12}}>
              Only tick tasks for the attachment you actually used this session.
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
              {eq.attachment_checklists.map((a) => {
                const key = a.name + ':' + a.kind + ':' + a.hours_or_km;
                const ticks = attachmentTicks[key] instanceof Set ? attachmentTicks[key] : new Set();
                const tasks = Array.isArray(a.tasks) ? a.tasks : [];
                const allTicked = tasks.length > 0 && ticks.size >= tasks.length;
                const anyTicked = ticks.size > 0;
                return (
                  <div
                    key={key}
                    style={{
                      borderRadius: 10,
                      background: allTicked ? '#eff6ff' : anyTicked ? 'var(--warn-soft)' : '#fafafa',
                      border: '1px solid ' + (allTicked ? '#bfdbfe' : anyTicked ? 'var(--warn-stripe)' : '#E6E8EB'),
                      padding: '12px 14px',
                    }}
                  >
                    <div style={{fontWeight: 700, color: 'var(--ink)', fontSize: 13, marginBottom: 6}}>
                      {a.name}{' '}
                      <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 500}}>
                        · {a.hours_or_km === 0 ? 'Every Use' : a.hours_or_km + (a.kind === 'km' ? 'km' : 'h')}
                      </span>
                    </div>
                    {a.help_text && (
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--text-2)',
                          background: 'var(--warn-soft)',
                          border: '1px solid #fde68a',
                          borderRadius: 10,
                          padding: '6px 8px',
                          marginBottom: 8,
                          fontStyle: 'italic',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {a.help_text}
                      </div>
                    )}
                    {tasks.length > 0 ? (
                      <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                        {tasks.map((t) => {
                          const ticked = ticks.has(t.id);
                          return (
                            <label
                              key={t.id}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 8,
                                padding: '6px 8px',
                                borderRadius: 10,
                                border: '1px solid ' + (ticked ? 'var(--brand)' : 'var(--border)'),
                                background: '#fff',
                                cursor: 'pointer',
                                fontSize: 12,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={ticked}
                                onChange={() => toggleAttachmentTask(key, t.id)}
                                style={{
                                  margin: '2px 0 0',
                                  flexShrink: 0,
                                  width: 16,
                                  height: 16,
                                  padding: 0,
                                  border: '1px solid var(--border-strong)',
                                }}
                              />
                              <span
                                style={{color: ticked ? 'var(--brand)' : 'var(--ink)', fontWeight: ticked ? 600 : 500}}
                              >
                                {t.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{fontSize: 11, color: 'var(--ink-faint)', fontStyle: 'italic'}}>
                        No tasks configured.
                      </div>
                    )}
                    {anyTicked && (
                      <div
                        style={{
                          fontSize: 11,
                          color: allTicked ? 'var(--info-ink)' : 'var(--warn-ink)',
                          marginTop: 6,
                          fontWeight: 600,
                        }}
                      >
                        {ticks.size} of {tasks.length} tasks ticked{allTicked ? ' · full completion' : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Photo upload (always shown when there's a reading — some tasks
            ask for "TAKE PICTURES SHOWING EACH SIDE"). */}
        {eq && hasReading && (
          <div style={cardS}>
            <div style={{fontSize: 13, fontWeight: 700, color: 'var(--ink)', marginBottom: 4}}>Photos</div>
            <div style={{fontSize: 11, color: 'var(--ink-muted)', marginBottom: 10}}>
              Attach photos of the machine, damage, or anything else the next team member should see.
            </div>
            {photos.length > 0 && (
              <div style={{display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10}}>
                {photos.map((p, i) => (
                  <div key={i} style={{position: 'relative'}}>
                    <PrivateStorageImage
                      bucket="equipment-maintenance-docs"
                      path={p.path}
                      legacyUrl={p.url}
                      alt={imageAltText(p.name, {fallback: 'Fueling photo', index: i, total: photos.length})}
                      style={{
                        width: 80,
                        height: 80,
                        objectFit: 'cover',
                        borderRadius: 10,
                        border: '1px solid var(--border)',
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      style={{
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        border: 'none',
                        background: 'var(--danger-ink)',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: 12,
                        lineHeight: 1,
                      }}
                    >
                      {'×'}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                for (const f of files) await uploadPhoto(f);
                e.target.value = '';
              }}
              disabled={uploadingPhoto}
              style={{fontSize: 13}}
            />
            {uploadingPhoto && (
              <div style={{fontSize: 11, color: 'var(--ink-faint)', marginTop: 6}}>Uploading{'…'}</div>
            )}
          </div>
        )}

        <div style={cardS}>
          <label style={lblS}>Comments / issues</label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            style={{...inpS, resize: 'vertical'}}
          />
        </div>

        {err && (
          <div
            style={{
              color: 'var(--danger-ink)',
              fontSize: 13,
              marginBottom: 10,
              padding: '8px 12px',
              background: 'var(--danger-soft)',
              borderRadius: 10,
            }}
          >
            {err}
          </div>
        )}

        <button
          onClick={submit}
          disabled={submitting || !eq}
          style={{
            width: '100%',
            padding: 14,
            borderRadius: 10,
            border: 'none',
            background: submitting || !eq ? 'var(--text-3)' : '#187A54',
            color: 'white',
            fontSize: 15,
            fontWeight: 700,
            cursor: submitting || !eq ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {submitting ? 'Saving…' : 'Save Fueling'}
        </button>
      </div>

      {stuckOpen && (
        <StuckSubmissionsModal
          rows={stuckRows}
          formLabel="fueling"
          onOpen={recovery.open}
          busy={submitting || recovery.opening}
          error={err}
          describeRow={(row) => {
            const args = (row.record as {args?: {parent_in?: Record<string, unknown>}} | null)?.args;
            const parent = args?.parent_in;
            const dateStr = parent ? parent.date : '?';
            const galStr = parent && parent.gallons != null ? `${parent.gallons} gal` : '?';
            const reading =
              parent && parent.hours_reading != null
                ? `${parent.hours_reading} h`
                : parent && parent.km_reading != null
                  ? `${parent.km_reading} km`
                  : '?';
            return `${dateStr} · ${galStr} · ${reading} (not yet sent)`;
          }}
          onRetry={async (csid) => {
            await retryStuck(csid);
          }}
          onDiscard={async (csid) => {
            await discardStuck(csid);
          }}
          onClose={() => setStuckOpen(false)}
        />
      )}
    </div>
  );
}
